<script setup>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Chip from "primevue/chip";
import FileUpload from "primevue/fileupload";
import Checkbox from "primevue/checkbox";
import { ref, computed, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useForm } from "@inertiajs/inertia-vue3";
import { usePage } from "@inertiajs/inertia-vue3";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";

const props = defineProps({
    visible: Boolean,
    recipients: {
        type: Array,
        default: () => []
    },
    currentFilter: {
        type: String,
        default: 'all'
    },
    currentGender: {
        type: String,
        default: 'all'
    },
    totalCustomers: {
        type: Number,
        default: 0
    },
    sendToAll: {
        type: Boolean,
        default: false
    },
    defaultTitle: {
        type: String,
        default: ""
    },
    defaultSummary: {
        type: String,
        default: ""
    },
    defaultMessage: {
        type: String,
        default: ""
    }
});

const emit = defineEmits(['update:visible', 'sent']);

const toast = useToast();
const page = usePage();

// Watch for errors on the page
watch(() => page.props?.errors, (errors) => {
    if (errors && Object.keys(errors).length > 0) {
        console.log('Page errors detected:', errors);
        Object.keys(errors).forEach(field => {
            const errorMessage = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
            toast.add({
                severity: 'error',
                summary: field === 'error' ? 'Error' : 'Validation Error',
                detail: errorMessage,
                life: 5000
            });
        });
    }
}, { deep: true, immediate: false });

const form = useForm({
    customer_ids: [],
    title: "",
    body: "",
    content: "",
    channels: ["push"],
    attachments: [],
    send_to_all: false,
    filter: null
});

watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        if (props.defaultTitle) form.title = props.defaultTitle;
        if (props.defaultSummary) form.body = props.defaultSummary;
        if (props.defaultMessage) form.content = props.defaultMessage;
    }
});

const fileUploadRef = ref(null);
const uploadingFiles = ref(false);
const uploadProgress = ref(0);

// Custom validator for file types
const isValidFileType = (value) => {
    if (!value || value.length === 0) return true; // Allow empty attachments
    return value.every(file => {
        // For Vapor uploads, check the stored mime type
        const mime = file.mime || '';
        return mime.startsWith('image/')
            || mime.startsWith('audio/')
            || mime.startsWith('video/')
            || mime === 'application/pdf'
            || mime.startsWith('text/')
            || mime === 'application/msword'
            || mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    });
};

// Validation rules
const rules = computed(() => ({
    title: {
        required: helpers.withMessage('Title is required', required)
    },
    body: {
        required: helpers.withMessage('Summary is required', required)
    },
    content: {
        required: helpers.withMessage('Message content is required', required)
    },
    channels: {
        required: helpers.withMessage('Select at least one delivery channel', value => {
            return Array.isArray(value) && value.length > 0;
        })
    },
    attachments: {
        isValidFileType: helpers.withMessage(
            'Supported file types: image, audio, video, PDF, text, DOC, DOCX',
            isValidFileType
        )
    }
}));

const v$ = useVuelidate(rules, form);

const isValid = computed(() => {
    return !v$.value.$invalid && props.recipients.length > 0;
});

const isAllCustomersSelected = computed(() => {
    // Use the sendToAll prop which is true only when "Select All" was clicked
    return props.sendToAll;
});

const handleRemoveRecipient = (index) => {
    // In a real implementation, this would update the parent's recipient list
    toast.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Recipient removal should be handled by parent component',
        life: 3000
    });
};

const handleFileUpload = async (event) => {
    const files = event.files;
    if (files && files.length > 0) {
        // Validate file types
        const allowedTypes = [
            'image/',
            'audio/',
            'video/',
            'text/',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        const invalidFiles = [];
        const validFiles = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const isAllowed = allowedTypes.some(type => file.type.startsWith(type));

            if (isAllowed) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        }

        // Show error message for invalid files
        if (invalidFiles.length > 0) {
            toast.add({
                severity: 'error',
                summary: 'Invalid File Type',
                detail: `Only images and audio files are allowed. Rejected: ${invalidFiles.join(', ')}`,
                life: 5000
            });
        }

        // Upload valid files to Vapor
        if (validFiles.length > 0) {
            uploadingFiles.value = true;
            uploadProgress.value = 0;

            try {
                for (const file of validFiles) {
                    const { key, url } = await window.Vapor.store(file, {
                        progress: progress => {
                            uploadProgress.value = Math.round(progress * 100);
                        }
                    });

                    // Store attachment with Vapor key and temporary URL
                    form.attachments.push({
                        name: file.name,
                        mime: file.type,
                        type: file.type.startsWith('image/')
                            ? 'image'
                            : file.type.startsWith('audio/')
                                ? 'audio'
                                : file.type.startsWith('video/')
                                    ? 'video'
                                    : 'file',
                        size: file.size,
                        key: key,
                        url: url,
                        isVapor: true
                    });
                }

                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: `${validFiles.length} file(s) uploaded successfully`,
                    life: 3000
                });
            } catch (error) {
                console.error('Vapor upload error:', error);
                toast.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: 'Failed to upload files. Please try again.',
                    life: 5000
                });
            } finally {
                uploadingFiles.value = false;
                uploadProgress.value = 0;
            }
        }

        // Trigger validation
        v$.value.attachments.$touch();
    }
};

const removeAttachment = (index) => {
    form.attachments.splice(index, 1);
    if (form.attachments.length === 0 && fileUploadRef.value) {
        fileUploadRef.value.clear();
    }
    // Re-validate attachments after removal
    v$.value.attachments.$touch();
};

const getFileIcon = (file) => {
    const type = (file.type || '').split('/')[0];
    if (type === 'image') return 'pi pi-image';
    if (type === 'audio') return 'pi pi-microphone';
    if (type === 'video') return 'pi pi-video';
    if ((file.mime || '').includes('pdf')) return 'pi pi-file-pdf';
    if ((file.mime || '').startsWith('text/')) return 'pi pi-file';
    return 'pi pi-file';
};

const handleSend = async () => {
    // Validate form
    const isFormValid = await v$.value.$validate();
    
    if (!isFormValid || props.recipients.length === 0) {
        if (props.recipients.length === 0) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select at least one recipient',
                life: 3000
            });
        }
        return;
    }
    
    // Ensure all fields are properly set
    form.attachments = form.attachments || [];
    
    // Create submit data based on whether "Select All" was used
    const submitData = {
        title: form.title,
        body: form.body,
        content: form.content,
        channels: form.channels,
        attachments: form.attachments
    };
    
    if (isAllCustomersSelected.value) {
        // When "Select All" was clicked in the dialog
        submitData.send_to_all = true;
        submitData.filter = props.currentFilter;
        submitData.gender = props.currentGender;
        // Don't send customer_ids when send_to_all is true
    } else {
        // When specific customers were selected
        submitData.send_to_all = false;
        submitData.customer_ids = props.recipients.map(r => r.id);
    }
    
    // Submit form using transform
    form.transform(() => submitData).post('/admin/notifications', {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Notification sent successfully!',
                life: 3000
            });
            closeDialog();
            emit('sent');
        },
        onError: (errors) => {
            // Errors are automatically set on form.errors by Inertia
            // They will be displayed inline like Vuelidate errors
        }
    });
};

const closeDialog = () => {
    emit('update:visible', false);
    // Reset form
    form.reset();
    form.attachments = []; // Explicitly clear attachments array
    form.channels = ["push"];
    if (fileUploadRef.value) {
        fileUploadRef.value.clear();
    }
    // Reset validation
    v$.value.$reset();
};

// Text formatting functions
const formatBold = () => {
    insertTextAtCursor('**', '**');
};

const formatItalic = () => {
    insertTextAtCursor('*', '*');
};

const formatLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
        insertTextAtCursor('[', `](${url})`);
    }
};

const formatList = () => {
    insertTextAtCursor('\n- ', '');
};

const insertTextAtCursor = (before, after) => {
    const textarea = document.querySelector('.compose-message-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = form.content.substring(start, end);

    const newText = form.content.substring(0, start) +
                   before + selectedText + after +
                   form.content.substring(end);

    form.content = newText;

    // Set cursor position
    setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
            start + before.length + selectedText.length,
            start + before.length + selectedText.length
        );
    }, 0);
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="closeDialog"
        modal
        :style="{ width: '600px' }"
        :draggable="false"
        :closable="false"
    >
        <template #header>
            <div class="flex justify-between items-center w-full">
                <h3 class="text-lg font-semibold">Compose Message</h3>
                <Button
                    icon="pi pi-times"
                    severity="secondary"
                    text
                    rounded
                    @click="closeDialog"
                />
            </div>
        </template>

        <div class="space-y-4">
            <!-- Recipients -->
            <div>
                <label class="block text-sm font-medium mb-2">To:</label>
                <div class="flex flex-wrap gap-2">
                    <Chip
                        v-for="(recipient, index) in recipients"
                        :key="recipient.id"
                        :label="recipient.name"
                        removable
                        @remove="handleRemoveRecipient(index)"
                    />
                </div>
                <small v-if="form.errors.customer_ids" class="p-error block mt-1">
                    {{ Array.isArray(form.errors.customer_ids) ? form.errors.customer_ids[0] : form.errors.customer_ids }}
                </small>
            </div>

            <!-- Title -->
            <div>
                <label class="block text-sm font-medium mb-2">Delivery Channels</label>
                <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                        <Checkbox
                            v-model="form.channels"
                            inputId="channelPush"
                            name="channels"
                            value="push"
                        />
                        <label for="channelPush" class="text-sm">Push</label>
                    </div>
                    <div class="flex items-center gap-2">
                        <Checkbox
                            v-model="form.channels"
                            inputId="channelEmail"
                            name="channels"
                            value="email"
                        />
                        <label for="channelEmail" class="text-sm">Email</label>
                    </div>
                </div>
                <small v-if="v$.channels.$error" class="p-error block mt-1">
                    {{ v$.channels.$errors[0].$message }}
                </small>
                <small v-else-if="form.errors.channels" class="p-error block mt-1">
                    {{ Array.isArray(form.errors.channels) ? form.errors.channels[0] : form.errors.channels }}
                </small>
            </div>

            <!-- Title -->
            <div>
                <label class="block text-sm font-medium mb-2">Title</label>
                <InputText
                    v-model="form.title"
                    placeholder="Enter title..."
                    class="w-full"
                    :class="{'p-invalid': v$.title.$error || form.errors.title}"
                    @blur="v$.title.$touch"
                />
                <small v-if="v$.title.$error" class="p-error block mt-1">
                    {{ v$.title.$errors[0].$message }}
                </small>
                <small v-else-if="form.errors.title" class="p-error block mt-1">
                    {{ Array.isArray(form.errors.title) ? form.errors.title[0] : form.errors.title }}
                </small>
            </div>

            <!-- Summary -->
            <div>
                <label class="block text-sm font-medium mb-2">Summary</label>
                <InputText
                    v-model="form.body"
                    placeholder="Enter summary..."
                    class="w-full"
                    :class="{'p-invalid': v$.body.$error}"
                    @blur="v$.body.$touch"
                />
                <small v-if="v$.body.$error" class="p-error block mt-1">
                    {{ v$.body.$errors[0].$message }}
                </small>
            </div>

            <!-- Message -->
            <div>
                <label class="block text-sm font-medium mb-2">Message</label>
                <div class="border rounded-lg">
                    <!-- Formatting toolbar -->
                    <div class="flex items-center gap-2 p-2 border-b bg-gray-50">
                        <button
                            @click="formatBold"
                            class="format-btn"
                            title="Bold"
                        >
                            <strong>B</strong>
                        </button>
                        <button
                            @click="formatItalic"
                            class="format-btn"
                            title="Italic"
                        >
                            <em>I</em>
                        </button>
                        <Button
                            icon="pi pi-link"
                            severity="secondary"
                            outlined
                            size="small"
                            @click="formatLink"
                            title="Insert Link"
                            class="formatting-button"
                        />
                        <Button
                            icon="pi pi-list"
                            severity="secondary"
                            outlined
                            size="small"
                            @click="formatList"
                            title="Bullet List"
                            class="formatting-button"
                        />
                    </div>
                    <Textarea
                        v-model="form.content"
                        placeholder="Write your message here..."
                        class="w-full border-0 compose-message-textarea"
                        :class="{'p-invalid': v$.content.$error}"
                        :autoResize="true"
                        rows="8"
                        @blur="v$.content.$touch"
                    />
                </div>
                <small v-if="v$.content.$error" class="p-error block mt-1">
                    {{ v$.content.$errors[0].$message }}
                </small>
            </div>

            <!-- File Uploads -->
            <div>
                <FileUpload
                    ref="fileUploadRef"
                    mode="basic"
                    accept="image/*,audio/*,video/*,application/pdf,text/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    :maxFileSize="10000000"
                    @select="handleFileUpload"
                    :auto="false"
                    :multiple="true"
                    chooseLabel="Add Attachment"
                    chooseIcon="pi pi-paperclip"
                    :showUploadButton="false"
                    :showCancelButton="false"
                    :class="{'p-invalid': v$.attachments.$error}"
                    :disabled="uploadingFiles"
                />
                <div v-if="uploadingFiles" class="mt-2">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-spin pi-spinner"></i>
                        <span class="text-sm">Uploading... {{ uploadProgress }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                            class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            :style="{ width: uploadProgress + '%' }"
                        ></div>
                    </div>
                </div>
                <small v-if="v$.attachments.$error" class="p-error block mt-1">
                    {{ v$.attachments.$errors[0].$message }}
                </small>
                <div v-if="form.attachments.length > 0" class="mt-2 space-y-2">
                    <div
                        v-for="(attachment, index) in form.attachments"
                        :key="index"
                        class="flex items-center gap-2"
                    >
                        <i :class="getFileIcon(attachment)" class="text-sm"></i>
                        <span class="text-sm">{{ attachment.name }}</span>
                        <span v-if="attachment.isVapor" class="text-xs text-green-600">(uploaded)</span>
                        <Button
                            icon="pi pi-times"
                            severity="danger"
                            text
                            size="small"
                            @click="removeAttachment(index)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    label="Send Message"
                    severity="primary"
                    @click.prevent="handleSend"
                    :disabled="!isValid || form.processing"
                    :loading="form.processing"
                    type="button"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.compose-message-textarea {
    min-height: 200px;
}

.format-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    transition: all 0.2s;
}

.format-btn:hover {
    background-color: #f3f4f6;
    border-color: #d1d5db;
}

.format-btn:active {
    background-color: #e5e7eb;
}

.format-btn strong {
    font-weight: 900;
}

.format-btn em {
    font-style: italic;
    font-family: serif;
    font-size: 16px;
}

.formatting-button {
    background-color: white !important;
    border-color: #e5e7eb !important;
}

.formatting-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #d1d5db !important;
}

.formatting-button .pi {
    color: #374151 !important;
}

.p-error {
    color: #dc2626;
    font-size: 0.875rem;
}

.p-invalid {
    border-color: #dc2626 !important;
}

.p-invalid:focus {
    box-shadow: 0 0 0 0.2rem rgba(220, 38, 38, 0.2) !important;
}
</style>
